import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate, useParams, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { SocialPreview } from "@/components/admin/SocialPreview";
import { uploadMediaFile } from "@/lib/admin/media/upload";
import { MediaStore } from "@/lib/admin/media/store";
import { useAuth } from "@/hooks/use-auth";
import {
  generateSocialCopyFn,
  generateSocialImageFn,
  getSocialPostFn,
  listSocialAccountsFn,
  publishSocialPostFn,
  saveSocialPostFn,
} from "@/lib/social/social.functions";
import { AI_ACTIONS, AI_TONES, IMAGE_SIZES, type AiAction, type AiTone, type ImageSizeId } from "@/lib/social/providers";
import { LIVE_PLATFORMS, platformLabel, type SocialAccount, type SocialPlatform, type SocialPost } from "@/lib/social/types";

export const Route = createFileRoute("/admin/social/$id")({
  head: () => ({
    meta: [
      { title: "Compose Post · Social Studio — CB16 Console" },
      { name: "description", content: "Compose, preview and publish a CB16 social post." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <RequirePermission perm="campaigns.write">
      <PostEditor />
    </RequirePermission>
  ),
});

const card = "rounded-sm border border-black/10 bg-white p-5";
const btn =
  "inline-flex items-center justify-center border border-black px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B] disabled:cursor-wait disabled:opacity-50";
const btnDark =
  "inline-flex items-center justify-center border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition hover:border-[#C6A15B] hover:bg-[#C6A15B] disabled:opacity-50";
const input =
  "w-full border border-black/15 bg-white px-3 py-2 text-sm text-black focus:border-[#C6A15B] focus:outline-none";
const label = "block text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50";

type MediaItem = { url: string; kind: "image" | "video"; alt: string; mediaAssetId: string | null };

function PostEditor() {
  const { id } = useParams({ from: "/admin/social/$id" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === "new";

  const getFn = useServerFn(getSocialPostFn);
  const saveFn = useServerFn(saveSocialPostFn);
  const publishFn = useServerFn(publishSocialPostFn);
  const accountsFn = useServerFn(listSocialAccountsFn);
  const copyFn = useServerFn(generateSocialCopyFn);
  const imageFn = useServerFn(generateSocialImageFn);

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [selected, setSelected] = useState<SocialPlatform[]>(["facebook"]);
  const [overrides, setOverrides] = useState<Partial<Record<SocialPlatform, string>>>({});
  const [accountFor, setAccountFor] = useState<Partial<Record<SocialPlatform, string>>>({});
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [post, setPost] = useState<SocialPost | null>(null);

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [tone, setTone] = useState<AiTone>("luxury");
  const [aiTarget, setAiTarget] = useState<SocialPlatform>("facebook");
  const [brief, setBrief] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageSize, setImageSize] = useState<ImageSizeId>("square");

  const [previewPlatform, setPreviewPlatform] = useState<SocialPlatform>("facebook");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const list = (await accountsFn({ data: undefined } as never)) as SocialAccount[];
      setAccounts(list);
      if (!isNew) {
        const p = (await getFn({ data: { id } })) as SocialPost | null;
        if (p) {
          setPost(p);
          setTitle(p.title);
          setCaption(p.caption);
          setLinkUrl(p.linkUrl ?? "");
          setScheduledAt(p.scheduledAt ? p.scheduledAt.slice(0, 16) : "");
          setSelected(p.platforms.map((x) => x.platform));
          setOverrides(Object.fromEntries(p.platforms.filter((x) => x.caption).map((x) => [x.platform, x.caption!])));
          setAccountFor(
            Object.fromEntries(p.platforms.filter((x) => x.accountId).map((x) => [x.platform, x.accountId!])),
          );
          setMedia(p.media.map((m) => ({ url: m.url, kind: m.kind, alt: m.alt, mediaAssetId: m.mediaAssetId })));
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load post.");
    } finally {
      setLoading(false);
    }
  }, [accountsFn, getFn, id, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  const accountsByPlatform = useMemo(() => {
    const map: Partial<Record<SocialPlatform, SocialAccount[]>> = {};
    for (const a of accounts) (map[a.platform] ??= []).push(a);
    return map;
  }, [accounts]);

  function togglePlatform(p: SocialPlatform) {
    setSelected((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  }

  function payload() {
    return {
      id: isNew ? undefined : id,
      title,
      caption,
      linkUrl: linkUrl || null,
      scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      platforms: selected.map((p) => ({
        platform: p,
        accountId: accountFor[p] ?? null,
        caption: overrides[p] ?? null,
      })),
      media: media.map((m) => ({ url: m.url, kind: m.kind, alt: m.alt, mediaAssetId: m.mediaAssetId })),
    };
  }

  async function run(key: string, fn: () => Promise<string | void>) {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      const out = await fn();
      if (typeof out === "string") setMessage(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(null);
    }
  }

  async function save(status: "draft" | "scheduled") {
    const saved = (await saveFn({ data: { post: payload(), status } })) as SocialPost;
    setPost(saved);
    if (isNew) navigate({ to: "/admin/social/$id", params: { id: saved.id }, replace: true });
    return status === "scheduled" ? "Post scheduled." : "Draft saved.";
  }

  async function publishNow() {
    const saved = (await saveFn({ data: { post: payload(), status: "draft" } })) as SocialPost;
    const res = (await publishFn({ data: { id: saved.id } })) as {
      ok: boolean;
      results: { platform: string; ok: boolean; error?: string }[];
    };
    if (isNew) navigate({ to: "/admin/social/$id", params: { id: saved.id }, replace: true });
    const fresh = (await getFn({ data: { id: saved.id } })) as SocialPost | null;
    setPost(fresh);
    const failures = res.results.filter((r) => !r.ok);
    if (failures.length > 0) {
      throw new Error(failures.map((f) => `${platformLabel(f.platform)}: ${f.error}`).join(" · "));
    }
    return "Published to " + res.results.map((r) => platformLabel(r.platform)).join(", ") + ".";
  }

  async function generate(action: AiAction) {
    const res = (await copyFn({
      data: { action, tone, platform: aiTarget, title, caption: overrides[aiTarget] ?? caption, brief },
    })) as { text: string };
    const text = res.text.trim();
    if (!text) return "AI returned nothing — try again.";
    if (action === "hashtags" || action === "cta") {
      const base = overrides[aiTarget] ?? caption;
      const merged = `${base}\n\n${text}`.trim();
      if (aiTarget === selected[0] && !overrides[aiTarget]) setCaption(merged);
      else setOverrides((o) => ({ ...o, [aiTarget]: merged }));
    } else if (overrides[aiTarget] !== undefined) {
      setOverrides((o) => ({ ...o, [aiTarget]: text }));
    } else {
      setCaption(text);
    }
    return "Copy generated.";
  }

  async function generateImage() {
    const asset = (await imageFn({ data: { prompt: imagePrompt, size: imageSize } })) as {
      id: string;
      url: string;
      filename: string;
    };
    setMedia((m) => [...m, { url: asset.url, kind: "image", alt: imagePrompt.slice(0, 120), mediaAssetId: asset.id }]);
    return `Generated ${asset.filename} and saved it to the Media Library.`;
  }

  async function uploadFiles(files: File[]) {
    for (const file of files) {
      const uploaded = await uploadMediaFile(file, "/social");
      const asset = await MediaStore.upload({
        filename: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        url: uploaded.url,
        storagePath: uploaded.path,
        width: uploaded.width,
        height: uploaded.height,
        uploadedBy: user?.email ?? user?.id,
      });
      setMedia((m) => [
        ...m,
        { url: asset.url, kind: file.type.startsWith("video") ? "video" : "image", alt: "", mediaAssetId: asset.id },
      ]);
    }
    return `Uploaded ${files.length} file(s).`;
  }

  const previewAccount =
    accounts.find((a) => a.id === accountFor[previewPlatform]) ??
    accounts.find((a) => a.platform === previewPlatform);
  const previewCaption = overrides[previewPlatform] ?? caption;
  const igSelectedWithoutMedia = selected.includes("instagram") && media.length === 0;

  if (loading) return <p className="py-10 text-sm text-black/50">Loading…</p>;

  return (
    <div className="space-y-6">
      <PageHeader
        title={isNew ? "New Post" : title || "Edit Post"}
        subtitle="Compose once, tailor per platform, preview, then schedule or publish."
        action={
          <div className="flex items-center gap-3">
            {post && <StatusPill tone={post.status === "published" ? "success" : "neutral"}>{post.status}</StatusPill>}
            <Link to="/admin/social" className={btn}>
              Back
            </Link>
          </div>
        }
      />

      {error && <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
      {message && (
        <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div>
      )}
      {accounts.length === 0 && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No social accounts are connected yet. You can draft and schedule posts now — connect Facebook and Instagram in{" "}
          <Link to="/admin/ai-social" className="underline">
            AI &amp; Social Integrations
          </Link>{" "}
          before publishing.
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-6">
          <section className={card}>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-black">Content</h3>
            <div className="space-y-4">
              <div>
                <label className={label} htmlFor="post-title">Post title</label>
                <input id="post-title" className={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Internal name" />
              </div>
              <div>
                <label className={label} htmlFor="post-caption">Caption</label>
                <textarea
                  id="post-caption"
                  className={`${input} min-h-[160px]`}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Write the shared caption…"
                />
              </div>
              <div>
                <label className={label} htmlFor="post-link">Link (Facebook only)</label>
                <input id="post-link" className={input} value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://cb16.in/fragrances/…" />
              </div>
            </div>
          </section>

          <section className={card}>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-black">Platforms</h3>
            <div className="space-y-4">
              {LIVE_PLATFORMS.map((p) => {
                const on = selected.includes(p.id);
                const list = accountsByPlatform[p.id] ?? [];
                return (
                  <div key={p.id} className="border border-black/10 p-4">
                    <label className="flex items-center gap-3 text-sm font-medium text-black">
                      <input type="checkbox" checked={on} onChange={() => togglePlatform(p.id)} />
                      {p.label}
                      <span className="text-[10px] uppercase tracking-[0.18em] text-black/40">
                        {list.length === 0
                          ? "No account connected"
                          : list.map((a) => a.name).join(" · ")}
                      </span>
                    </label>
                    {on && (
                      <div className="mt-3 space-y-3">
                        <select
                          className={input}
                          value={accountFor[p.id] ?? ""}
                          onChange={(e) => setAccountFor((a) => ({ ...a, [p.id]: e.target.value }))}
                        >
                          <option value="">Default connected account</option>
                          {list.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name} {a.username ? `(@${a.username})` : ""}
                            </option>
                          ))}
                        </select>
                        <div>
                          <label className={label} htmlFor={`ovr-${p.id}`}>
                            {p.label} caption override ({(overrides[p.id] ?? caption).length}/{p.captionLimit})
                          </label>
                          <textarea
                            id={`ovr-${p.id}`}
                            className={`${input} min-h-[90px]`}
                            value={overrides[p.id] ?? ""}
                            onChange={(e) => setOverrides((o) => ({ ...o, [p.id]: e.target.value }))}
                            placeholder="Leave empty to use the shared caption"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-black/50">
                LinkedIn, X, Pinterest, Google Business Profile, YouTube Community, TikTok and Threads are reserved in the
                data model and will appear here once their adapters ship.
              </p>
            </div>
          </section>

          <section className={card}>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-black">Media</h3>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={btn} onClick={() => setPickerOpen(true)}>
                Choose from Media Library
              </button>
              <button type="button" className={btn} onClick={() => fileRef.current?.click()} disabled={busy === "upload"}>
                Upload image / video
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  if (fileRef.current) fileRef.current.value = "";
                  if (files.length) void run("upload", () => uploadFiles(files));
                }}
              />
            </div>
            {igSelectedWithoutMedia && (
              <p className="mt-3 text-xs text-amber-700">Instagram requires at least one image or video.</p>
            )}
            {media.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {media.map((m, i) => (
                  <div key={m.url + i} className="relative border border-black/10">
                    {m.kind === "video" ? (
                      <video src={m.url} className="aspect-square w-full object-cover" muted />
                    ) : (
                      <img src={m.url} alt={m.alt} className="aspect-square w-full object-cover" />
                    )}
                    <button
                      type="button"
                      className="absolute right-1 top-1 bg-black/70 px-2 py-0.5 text-[10px] uppercase text-white"
                      onClick={() => setMedia((cur) => cur.filter((_, idx) => idx !== i))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={card}>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-black">AI content</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className={label} htmlFor="ai-target">Generate for</label>
                <select id="ai-target" className={input} value={aiTarget} onChange={(e) => setAiTarget(e.target.value as SocialPlatform)}>
                  {LIVE_PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="ai-tone">Tone</label>
                <select id="ai-tone" className={input} value={tone} onChange={(e) => setTone(e.target.value as AiTone)}>
                  {AI_TONES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="ai-brief">Extra brief</label>
                <input id="ai-brief" className={input} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Optional angle" />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {AI_ACTIONS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={btn}
                  disabled={busy === "ai" + a.id}
                  onClick={() => void run("ai" + a.id, () => generate(a.id))}
                >
                  {busy === "ai" + a.id ? "Working…" : a.label}
                </button>
              ))}
            </div>

            <div className="mt-6 border-t border-black/10 pt-5">
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-black/60">AI image</h4>
              <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
                <input className={input} value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} placeholder="Describe the image…" />
                <select className={input} value={imageSize} onChange={(e) => setImageSize(e.target.value as ImageSizeId)}>
                  {IMAGE_SIZES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className={`${btn} mt-3`}
                disabled={busy === "img" || !imagePrompt.trim()}
                onClick={() => void run("img", generateImage)}
              >
                {busy === "img" ? "Generating…" : "Generate image"}
              </button>
            </div>

            <div className="mt-6 border-t border-black/10 pt-5">
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-black/60">AI video (Phase 2)</h4>
              <p className="mb-3 text-xs text-black/50">
                Reel, Story and Product Video generation are architected but not enabled yet.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Generate Reel", "Generate Story", "Generate Product Video"].map((l) => (
                  <button key={l} type="button" className={`${btn} opacity-50`} disabled title="Coming in Phase 2">
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className={card}>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-black">Publish</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="sched">Schedule for</label>
                <input id="sched" type="datetime-local" className={input} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" className={btn} disabled={busy === "draft"} onClick={() => void run("draft", () => save("draft"))}>
                {busy === "draft" ? "Saving…" : "Save draft"}
              </button>
              <button
                type="button"
                className={btn}
                disabled={busy === "sched" || !scheduledAt}
                onClick={() => void run("sched", () => save("scheduled"))}
              >
                {busy === "sched" ? "Scheduling…" : "Schedule"}
              </button>
              <button
                type="button"
                className={btnDark}
                disabled={busy === "pub" || selected.length === 0}
                onClick={() => void run("pub", publishNow)}
              >
                {busy === "pub" ? "Publishing…" : "Publish now"}
              </button>
            </div>
            {post && post.platforms.some((p) => p.remotePermalink) && (
              <ul className="mt-4 space-y-1 text-xs">
                {post.platforms
                  .filter((p) => p.remotePermalink)
                  .map((p) => (
                    <li key={p.id}>
                      <a href={p.remotePermalink!} target="_blank" rel="noreferrer" className="text-[#8a6b2f] underline">
                        View on {platformLabel(p.platform)}
                      </a>
                    </li>
                  ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className={card}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">Preview</h3>
              <div className="flex gap-1">
                {(["desktop", "mobile"] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDevice(d)}
                    className={
                      "border px-2 py-1 text-[10px] uppercase tracking-[0.16em] " +
                      (device === d ? "border-[#C6A15B] text-[#8a6b2f]" : "border-black/15 text-black/50")
                    }
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4 flex gap-1">
              {LIVE_PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPreviewPlatform(p.id)}
                  className={
                    "border px-2 py-1 text-[10px] uppercase tracking-[0.16em] " +
                    (previewPlatform === p.id ? "border-[#C6A15B] text-[#8a6b2f]" : "border-black/15 text-black/50")
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex justify-center">
              <SocialPreview
                platform={previewPlatform}
                device={device}
                accountName={previewAccount?.username || previewAccount?.name || ""}
                caption={previewCaption}
                media={media}
              />
            </div>
          </div>
        </aside>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Choose media for this post"
        onSelect={(asset) =>
          setMedia((m) => [
            ...m,
            {
              url: asset.url,
              kind: asset.mime.startsWith("video") ? "video" : "image",
              alt: asset.alt,
              mediaAssetId: asset.id,
            },
          ])
        }
      />
    </div>
  );
}